import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createDownloadFileFormOnClick } from 'library/utils/helpers/FileHelper';
import { useCheckConsoleDebuggingReadAccess } from 'main/auth/checkUserAccess';
import {
	AuthProviderStorage,
	AuthenticationProvider,
} from 'main/auth/provider';

interface UsePdfDownloadSubmitParams<T> {
	/** Endpoint the hidden download form posts to. */
	url: string;
	/** Returns the downloaded file name. */
	getFileName: () => string;
	/** Builds the form data (PDF request) posted to the endpoint. */
	buildFormData: (formData: T) => object;
	/** Called after the download form was submitted successfully. */
	onSuccess?: () => void;
}

/**
 * Generic PDF download submit shared by PDF print dialogs. Resolves the auth
 * token and triggers a hidden form post that downloads the generated PDF.
 */
export const usePdfDownloadSubmit = <T>({
	url,
	getFileName,
	buildFormData,
	onSuccess,
}: UsePdfDownloadSubmitParams<T>) => {
	const { t } = useTranslation('common');
	const { enqueueSnackbar } = useSnackbar();
	const { checkConsoleDebuggingReadAccess } =
		useCheckConsoleDebuggingReadAccess();

	const [isPending, setIsPending] = useState(false);
	const [provider] = useState<AuthenticationProvider>(
		AuthProviderStorage.getInstance().getProvider()
	);

	const submit = async (formData: T) => {
		setIsPending(true);
		try {
			const token = await provider.getTokenAsync();

			createDownloadFileFormOnClick(
				token,
				url,
				getFileName(),
				buildFormData(formData)
			);

			onSuccess?.();
			setIsPending(false);
		} catch (error) {
			setIsPending(false);
			if (checkConsoleDebuggingReadAccess()) {
				console.error(error);
			}

			enqueueSnackbar(t('error-unexpected'), {
				variant: 'error',
				style: { whiteSpace: 'pre-line' },
			});
		}
	};

	return { submit, isPending };
};
